/**
 * Brand-wide contact info (WhatsApp number, support emails) used across the
 * public footer pages (Contacto, FAQ, Carreras, Prensa).
 *
 * Source of truth = backend `GET /api/settings/company` (singleton doc that
 * admins edit from Admin → Ajustes). We cache the response in module-level
 * memory + sessionStorage so all pages render instantly while still picking
 * up admin-edited values on the next session/page load.
 */

import { useEffect, useState } from "react";
import { api } from "../App";

const STORAGE_KEY = "rivones_company_contact_v1";

export const DEFAULT_COMPANY_CONTACT = {
  whatsapp_number: "5216644854055",
  support_email: "info@rivones.com",
  press_email: "prensa@rivones.com",
  careers_email: "carreras@rivones.com",
};

let cache = null;
let inflight = null;

function readSession() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULT_COMPANY_CONTACT, ...JSON.parse(raw) };
  } catch (_) { /* ignore */ }
  return null;
}

function writeSession(value) {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value)); } catch (_) { /* ignore */ }
}

export async function fetchCompanyContact() {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = api
    .get("/settings/company")
    .then((r) => {
      cache = { ...DEFAULT_COMPANY_CONTACT, ...(r.data || {}) };
      writeSession(cache);
      return cache;
    })
    .catch(() => {
      cache = readSession() || { ...DEFAULT_COMPANY_CONTACT };
      return cache;
    })
    .finally(() => { inflight = null; });
  return inflight;
}

export function clearCompanyContactCache() {
  cache = null;
  try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) { /* ignore */ }
}

/**
 * React hook: returns `{ contact, loading }`. Initial value is the cached /
 * persisted copy (or defaults) so links render immediately without flicker;
 * the freshest value from the API replaces it once the request resolves.
 */
export function useCompanyContact() {
  const [contact, setContact] = useState(
    () => cache || readSession() || { ...DEFAULT_COMPANY_CONTACT }
  );
  const [loading, setLoading] = useState(!cache);

  useEffect(() => {
    let active = true;
    fetchCompanyContact().then((c) => {
      if (active) {
        setContact(c);
        setLoading(false);
      }
    });
    return () => { active = false; };
  }, []);

  return { contact, loading };
}

export function waLink(number, message) {
  const digits = String(number || "").replace(/[^0-9]/g, "");
  const base = `https://wa.me/${digits}`;
  return message ? `${base}?text=${encodeURIComponent(message)}` : base;
}
